/**
 * 3D Azure Globe with Three.js
 * Shows 13 hemispheres as clickable markers with live payment data overlays
 * Observer marker at equator
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';
import { gsap } from 'gsap';

class AzureGlobe {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      radius: 5,
      autoRotate: true,
      rotateSpeed: 0.5,
      markerScale: 0.15,
      observerScale: 0.2,
      ...options
    };
    
    this.hemispheres = [];
    this.markers = [];
    this.observerMarker = null;
    this.paymentData = new Map();
    this.selectedHemisphere = null;
    
    this.init();
  }

  init() {
    this.setupScene();
    this.createGlobe();
    this.createLighting();
    this.createControls();
    this.createRenderer();
    this.createObserver();
    
    this.animate();
    this.setupEventListeners();
  }

  setupScene() {
    // Scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x000814);
    
    // Camera
    const aspect = this.container.clientWidth / this.container.clientHeight;
    this.camera = new THREE.PerspectiveCamera(45, aspect, 0.1, 1000);
    this.camera.position.set(0, 0, 15);
    
    // Clock for animations
    this.clock = new THREE.Clock();
  }

  createGlobe() {
    // Create sphere geometry
    const geometry = new THREE.SphereGeometry(this.options.radius, 64, 64);
    
    // Create Azure-style material
    const material = new THREE.MeshPhongMaterial({
      color: 0x0078d4,
      emissive: 0x0078d4,
      emissiveIntensity: 0.1,
      shininess: 100,
      specular: 0x222222,
      wireframe: false
    });
    
    this.globe = new THREE.Mesh(geometry, material);
    this.scene.add(this.globe);
    
    // Add wireframe overlay
    const wireframeGeometry = new THREE.SphereGeometry(this.options.radius * 1.01, 32, 32);
    const wireframeMaterial = new THREE.MeshBasicMaterial({
      color: 0x40e0d0,
      wireframe: true,
      transparent: true,
      opacity: 0.1
    });
    
    const wireframe = new THREE.Mesh(wireframeGeometry, wireframeMaterial);
    this.globe.add(wireframe);
    
    // Add atmospheric glow
    this.createAtmosphere();
  }

  createAtmosphere() {
    const atmosphereGeometry = new THREE.SphereGeometry(this.options.radius * 1.2, 64, 64);
    const atmosphereMaterial = new THREE.ShaderMaterial({
      uniforms: {
        glowColor: { value: new THREE.Color(0x0078d4) },
        viewVector: { value: this.camera.position }
      },
      vertexShader: `
        varying vec3 vNormal;
        void main() {
          vNormal = normalize(normalMatrix * normal);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float c;
        uniform vec3 glowColor;
        varying vec3 vNormal;
        void main() {
          float intensity = pow(0.7 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 2.0);
          gl_FragColor = vec4(glowColor, 1.0) * intensity;
        }
      `,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
      transparent: true
    });
    
    const atmosphere = new THREE.Mesh(atmosphereGeometry, atmosphereMaterial);
    this.scene.add(atmosphere);
  }

  createLighting() {
    // Ambient light
    const ambientLight = new THREE.AmbientLight(0x404040, 0.5);
    this.scene.add(ambientLight);
    
    // Main directional light
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 3, 5);
    this.scene.add(directionalLight);
    
    // Point light for glow effect
    const pointLight = new THREE.PointLight(0x0078d4, 1, 100);
    pointLight.position.set(0, 0, 10);
    this.scene.add(pointLight);
  }

  createControls() {
    this.controls = new OrbitControls(this.camera, this.container);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.minDistance = 8;
    this.controls.maxDistance = 30;
    this.controls.autoRotate = this.options.autoRotate;
    this.controls.autoRotateSpeed = this.options.rotateSpeed;
  }

  createRenderer() {
    // Main renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.container.appendChild(this.renderer.domElement);
    
    // CSS2D renderer for labels
    this.labelRenderer = new CSS2DRenderer();
    this.labelRenderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.labelRenderer.domElement.style.position = 'absolute';
    this.labelRenderer.domElement.style.top = '0';
    this.labelRenderer.domElement.style.pointerEvents = 'none';
    this.container.appendChild(this.labelRenderer.domElement);
  }

  createObserver() {
    // Create observer marker at equator
    const observerGeometry = new THREE.SphereGeometry(this.options.observerScale, 16, 16);
    const observerMaterial = new THREE.MeshPhongMaterial({
      color: 0xffd700,
      emissive: 0xffd700,
      emissiveIntensity: 0.5
    });
    
    this.observerMarker = new THREE.Mesh(observerGeometry, observerMaterial);
    
    // Position at equator (0 degrees latitude, 0 degrees longitude)
    const observerPosition = this.latLongToVector3(0, 0, this.options.radius + 0.5);
    this.observerMarker.position.copy(observerPosition);
    
    this.scene.add(this.observerMarker);
    
    // Add observer label
    const observerLabel = this.createLabel('Observer', 0xffd700);
    observerLabel.position.copy(observerPosition);
    observerLabel.position.y += 1;
    this.scene.add(observerLabel);
  }

  createHemisphereMarkers(hemisphereData) {
    // Clear existing markers
    this.clearMarkers();
    
    this.hemispheres = hemisphereData;
    
    hemisphereData.forEach((hemisphere, index) => {
      this.createHemisphereMarker(hemisphere, index);
    });
  }

  createHemisphereMarker(hemisphere, index) {
    // Create marker geometry
    const geometry = new THREE.SphereGeometry(this.options.markerScale, 16, 16);
    const material = new THREE.MeshPhongMaterial({
      color: new THREE.Color().setHSL(index / 13, 0.7, 0.5),
      emissive: new THREE.Color().setHSL(index / 13, 0.7, 0.3),
      emissiveIntensity: 0.3
    });
    
    const marker = new THREE.Mesh(geometry, material);
    
    // Calculate position from latitude/longitude
    const position = this.latLongToVector3(
      hemisphere.latitude,
      hemisphere.longitude,
      this.options.radius + 0.3
    );
    
    marker.position.copy(position);
    marker.userData = { hemisphere, index };
    
    // Add pulsing animation
    this.animateMarker(marker, hemisphere);
    
    this.scene.add(marker);
    this.markers.push(marker);
    
    // Create label
    const label = this.createLabel(hemisphere.name, 0xffffff);
    label.position.copy(position);
    label.position.y += 1;
    this.scene.add(label);
    
    // Add click interaction
    marker.cursor = 'pointer';
    marker.addEventListener('click', () => {
      this.onHemisphereClick(hemisphere);
    });
  }

  createLabel(text, color) {
    const div = document.createElement('div');
    div.className = 'hemisphere-label';
    div.textContent = text;
    div.style.color = `#${color.getHexString()}`;
    div.style.fontSize = '12px';
    div.style.fontWeight = 'bold';
    div.style.textShadow = '0 0 4px rgba(0,0,0,0.8)';
    div.style.pointerEvents = 'none';
    
    const label = new CSS2DObject(div);
    return label;
  }

  animateMarker(marker, hemisphere) {
    // Get payment data for this hemisphere
    const paymentInfo = this.paymentData.get(hemisphere.id) || { 
      transactions: 0, 
      verified: 0, 
      amount: 0 
    };
    
    // Scale based on activity
    const scale = this.options.markerScale * (1 + paymentInfo.transactions * 0.01);
    
    gsap.to(marker.scale, {
      x: scale,
      y: scale,
      z: scale,
      duration: 1,
      ease: "power2.inOut",
      repeat: -1,
      yoyo: true
    });
    
    // Update color based on verification rate
    const verificationRate = paymentInfo.transactions > 0 
      ? paymentInfo.verified / paymentInfo.transactions 
      : 0;
    
    const hue = verificationRate * 0.3; // Green for high verification, red for low
    marker.material.color.setHSL(hue, 0.8, 0.5);
  }

  latLongToVector3(lat, lon, radius) {
    const phi = (90 - lat) * (Math.PI / 180);
    const theta = (lon + 180) * (Math.PI / 180);
    
    const x = -radius * Math.sin(phi) * Math.cos(theta);
    const y = radius * Math.cos(phi);
    const z = radius * Math.sin(phi) * Math.sin(theta);
    
    return new THREE.Vector3(x, y, z);
  }

  updatePaymentData(hemisphereId, paymentData) {
    this.paymentData.set(hemisphereId, paymentData);
    
    // Update corresponding marker
    const marker = this.markers.find(m => m.userData.hemisphere.id === hemisphereId);
    if (marker) {
      this.animateMarker(marker, marker.userData.hemisphere);
    }
  }

  onHemisphereClick(hemisphere) {
    this.selectedHemisphere = hemisphere;
    
    // Emit event or callback
    if (this.options.onHemisphereClick) {
      this.options.onHemisphereClick(hemisphere);
    }
    
    // Visual feedback
    this.highlightHemisphere(hemisphere);
  }

  highlightHemisphere(hemisphere) {
    const marker = this.markers.find(m => m.userData.hemisphere.id === hemisphere.id);
    if (marker) {
      // Pulse effect
      gsap.to(marker.scale, {
        x: this.options.markerScale * 2,
        y: this.options.markerScale * 2,
        z: this.options.markerScale * 2,
        duration: 0.3,
        ease: "power2.out",
        yoyo: true,
        repeat: 1
      });
    }
  }

  clearMarkers() {
    this.markers.forEach(marker => {
      this.scene.remove(marker);
    });
    this.markers = [];
  }

  setupEventListeners() {
    // Handle window resize
    window.addEventListener('resize', () => {
      this.onWindowResize();
    });
    
    // Handle mouse move for hover effects
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    
    this.container.addEventListener('mousemove', (event) => {
      this.onMouseMove(event);
    });
  }

  onWindowResize() {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    
    this.renderer.setSize(width, height);
    this.labelRenderer.setSize(width, height);
  }

  onMouseMove(event) {
    const rect = this.container.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    
    // Check for hover over markers
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObjects(this.markers);
    
    // Reset all markers
    this.markers.forEach(marker => {
      marker.material.emissiveIntensity = 0.3;
    });
    
    // Highlight hovered marker
    if (intersects.length > 0) {
      const hoveredMarker = intersects[0].object;
      hoveredMarker.material.emissiveIntensity = 0.8;
      this.container.style.cursor = 'pointer';
    } else {
      this.container.style.cursor = 'default';
    }
  }

  animate() {
    requestAnimationFrame(() => this.animate());
    
    const elapsedTime = this.clock.getElapsedTime();
    
    // Rotate globe
    if (this.options.autoRotate) {
      this.globe.rotation.y = elapsedTime * 0.05;
    }
    
    // Update controls
    this.controls.update();
    
    // Render
    this.renderer.render(this.scene, this.camera);
    this.labelRenderer.render(this.scene, this.camera);
  }

  destroy() {
    // Clean up
    this.renderer.dispose();
    this.labelRenderer.dispose();
    this.controls.dispose();
    
    // Remove event listeners
    window.removeEventListener('resize', this.onWindowResize);
    
    // Clear scene
    this.scene.clear();
  }
}

export default AzureGlobe;
